/* Copyright (c) 2020-present CloudIO Inc */

import { useLatest } from '@cloudio-saas/statex';
import { useEffect } from 'react';

function useEventListener<K extends keyof WindowEventMap>(
  eventName: K,
  handler: (event: WindowEventMap[K]) => void,
  element: Window,
  capture: boolean,
): void;

function useEventListener<K extends keyof DocumentEventMap>(
  eventName: K,
  handler: (event: DocumentEventMap[K]) => void,
  element: Document,
  capture: boolean,
): void;

function useEventListener<K extends string>(
  eventName: K,
  handler: (event: unknown) => void,
  element: Document | Window = document,
  capture: boolean,
): void {
  const savedHandler = useLatest(handler);

  useEffect(
    () => {
      // Make sure element supports addEventListener
      const isSupported = element?.addEventListener;
      if (!isSupported) return;

      // Create event listener that calls handler function stored in ref
      const eventListener = (event: unknown) => savedHandler.current(event);

      // Add event listener
      element.addEventListener(eventName, eventListener, !!capture);

      // Remove event listener on cleanup
      return () => {
        element.removeEventListener(eventName, eventListener, !!capture);
      };
    },
    [eventName, element, capture, savedHandler], // Re-run if eventName or element changes
  );
}

export default useEventListener;
